/**
 * Sorts an array of objects based on a numeric property.
 * Null, undefined, or non-numeric values are always placed at the end of the result array.
 *
 * @param array - The array of objects to sort.
 * @param property - The key of the numeric property to sort by.
 * @param order - The sorting order, either 'asc' (ascending) or 'desc' (descending). Defaults to 'desc'.
 * @returns A new array sorted by the specified numeric property.
 */
export function sortByNumericProperty<T>(
  array: T[],
  property: keyof T,
  order: 'asc' | 'desc',
): T[] {
  // Use slice() to create a shallow copy of the array to avoid mutating the original
  return array.slice().sort((a, b) => {
    const aValue = a[property];
    const bValue = b[property];

    // Handle null, undefined, or non-numeric values
    const aIsInvalid = aValue == null || isNaN(Number(aValue));
    const bIsInvalid = bValue == null || isNaN(Number(bValue));

    if (aIsInvalid && bIsInvalid) {
      return 0; // Both invalid, keep relative order
    }
    if (aIsInvalid) {
      return 1; // Place `a` after `b`
    }
    if (bIsInvalid) {
      return -1; // Place `b` after `a`
    }

    // Compare numeric values
    const aNumeric = Number(aValue);
    const bNumeric = Number(bValue);

    return order === 'asc' ? aNumeric - bNumeric : bNumeric - aNumeric;
  });
}
